import { JSX } from 'react';
import { Box, Text, Stack, Flex, Link } from '@chakra-ui/react';
import { Span, Icon } from 'shared-components';
import {
  getFulfilmentProp,
  getEnglishLanguageResultsFromApplication,
  IEnglishTestData,
} from '@/utilities/fulfilmentData';
import { FulfilmentRendererProps } from './BaseFulfilmentDataRenderer';
import { EnglishTestType } from 'api-paths-cas';
import { faPaperclip } from '@fortawesome/pro-regular-svg-icons';
import type { Application } from 'api-paths-cas';

const CustomEnglishTestResults = ({
  englishTestData,
  application,
}: {
  englishTestData: IEnglishTestData;
  application: Application;
}): JSX.Element => {
  const { text, subtext } = getEnglishLanguageResultsFromApplication(englishTestData, application);

  return (
    <Stack spacing={1}>
      {text && <Box mb={0}>{text}</Box>}
      {subtext && (
        <Span color="muted" fontSize="sm">
          {subtext}
        </Span>
      )}
    </Stack>
  );
};

const CustomDocumentLinks = ({
  urls,
  openFile,
  testType,
}: {
  urls: string[];
  openFile: (urls: string[]) => void;
  testType?: string;
}): JSX.Element => {
  const linkText = testType ? `${testType} certificate` : 'English test certificate';

  return (
    <Flex alignItems="center" gap={2}>
      <Icon icon={faPaperclip} />
      <Link
        onClick={() => openFile(urls)}
        fontWeight="medium"
        color="blue.600"
        _hover={{ textDecoration: 'underline' }}
      >
        {linkText} ({urls.length} file{urls.length > 1 ? 's' : ''})
      </Link>
    </Flex>
  );
};

/**
 * Resolves the most current English test data. The application record
 * is preferred because the backend enriches it with the resolved English
 * test data from all sources using priority-based logic. The fulfilment
 * snapshot may be stale from an earlier requirement submission.
 */
function resolveEnglishTestData(
  fulfilmentEnglishTest: IEnglishTestData | null,
  application: Application,
): IEnglishTestData | null {
  // Application is the freshest enriched source; use it whenever present.
  if (application?.englishTest) {
    return application.englishTest as IEnglishTestData;
  }
  return fulfilmentEnglishTest ?? null;
}

/**
 * Returns the display name of the English test type (e.g. "IELTS Academic"),
 * using the custom typeOther value when the test type is "Other".
 */
function getTestType(englishTestData: IEnglishTestData | null): string | undefined {
  if (!englishTestData?.hasTakenTest) {
    return undefined;
  }
  if (englishTestData.type === EnglishTestType.Other) {
    return englishTestData.typeOther;
  }
  return englishTestData.type;
}

export default function EnglishLanguageResultsFulfilmentRenderer({
  fulfilmentData,
  application,
  openFile,
}: FulfilmentRendererProps): JSX.Element {
  const fulfilmentEnglishTest = getFulfilmentProp(fulfilmentData, 'englishTest') as IEnglishTestData | null;

  // Use the most current English test data: prefer whichever source has hasTakenTest === true.
  // The fulfilmentData may be a stale snapshot from offer creation, while the application
  // may have been updated later via the sections form.
  const englishTestData = resolveEnglishTestData(fulfilmentEnglishTest, application);

  const s3KeysRaw = getFulfilmentProp(fulfilmentData, 's3Keys');
  const s3Keys = Array.isArray(s3KeysRaw) ? s3KeysRaw : [];
  const hasDocuments = s3Keys.length > 0;

  const documentUrlsRaw = getFulfilmentProp(fulfilmentData, 'urls');
  const documentUrls: string[] = Array.isArray(documentUrlsRaw) ? (documentUrlsRaw as string[]) : [];

  const testType = getTestType(englishTestData);

  return (
    <>
      {(fulfilmentData || englishTestData) && (
        <Box>
          {englishTestData && (
            <Box>
              <CustomEnglishTestResults englishTestData={englishTestData} application={application} />

              {englishTestData.hasTakenTest && documentUrls.length > 0 && (
                <CustomDocumentLinks urls={documentUrls} openFile={openFile} testType={testType} />
              )}
            </Box>
          )}

          {hasDocuments && !englishTestData?.hasTakenTest && documentUrls.length > 0 && (
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="green.600" mb={1}>
                Results Documents
              </Text>
              <CustomDocumentLinks urls={documentUrls} openFile={openFile} testType={testType} />
            </Box>
          )}
        </Box>
      )}

      {getFulfilmentProp(fulfilmentData, 'sKey') && !s3Keys.length && (
        <Span color="muted">Results link not available</Span>
      )}

      {!fulfilmentData && !englishTestData && <Span color="muted">Not provided</Span>}
    </>
  );
}
